import { z } from "zod";
import { UNIT_SYMBOLS } from "./units";
import { ActivityKindSchema, LocationSchema, MethodSchema, ScenarioSchema, StageSchema } from "./scenario";

/* ── 배출계수 ────────────────────────────────────────────────── */

/** 자료 상태 , 화면에 배지로 나간다. placeholder 는 "임시값(전문가 검토 전)" */
export const DATA_STATUS = ["placeholder", "reviewed", "confirmed"] as const;
export const DataStatusSchema = z.enum(DATA_STATUS);
export type DataStatus = z.infer<typeof DataStatusSchema>;
export const DATA_STATUS_LABEL: Record<DataStatus, string> = {
  placeholder: "임시값(전문가 검토 전)",
  reviewed: "전문가 검토 완료",
  confirmed: "발주기관 확인 완료",
};

export const EF_SCOPES = ["general", "cert", "both"] as const;
export const EfScopeSchema = z.enum(EF_SCOPES);
export type EfScope = z.infer<typeof EfScopeSchema>;

export const EmissionFactorSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  kind: ActivityKindSchema,
  value: z.number().nonnegative(),
  /** "kgCO2eq/<분모단위>" , 분모는 units.ts 의 단위여야 한다 */
  unit: z.string().regex(/^kgCO2eq\/.+$/),
  source: z.string().min(1),
  sourceUrl: z.string().optional(),
  baseYear: z.number().int(),
  scope: EfScopeSchema.default("both"),
  gwpBasis: z.string().default("IPCC AR5 100년"),
  status: DataStatusSchema.default("placeholder"),
  /** 같은 kind,scope 안에서 기본으로 붙는 계수 */
  isDefault: z.boolean().default(false),
  note: z.string().optional(),
  active: z.boolean().default(true),
});
export type EmissionFactor = z.infer<typeof EmissionFactorSchema>;

/* ── 전력원 ──────────────────────────────────────────────────── */

export const ElectricitySourceSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  /** 이 전력원에 붙는 배출계수(kind=electricity) */
  efId: z.string().min(1),
  description: z.string().default(""),
  /** 청정수소 인증제 모의산정에서 재생에너지로 인정(교육용 단순화) */
  certRenewable: z.boolean().default(false),
  active: z.boolean().default(true),
});
export type ElectricitySource = z.infer<typeof ElectricitySourceSchema>;

/* ── 대상기술 ────────────────────────────────────────────────── */

export const TechnologySchema = z.object({
  id: z.string().min(1),
  stage: StageSchema,
  name: z.string().min(1),
  description: z.string().default(""),
  /** placeholder 는 팔레트에 자리만 있고 값은 협의 후 채운다 */
  status: z.enum(["ready", "placeholder"]).default("ready"),
  order: z.number().int().default(0),
  active: z.boolean().default(true),
});
export type Technology = z.infer<typeof TechnologySchema>;

/* ── 설비 템플릿 ─────────────────────────────────────────────── */

export const TemplateInputSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  kind: ActivityKindSchema,
  unit: z.enum(UNIT_SYMBOLS),
  /** 기본값. null 이면 '자료 미입력' 으로 시작한다 */
  defaultAmount: z.number().nullable(),
  /** perKgH2: 설비를 지나는 수소 1kg 당 값이라 기준량에 비례해 늘린다. fixed: 그대로 */
  scaling: z.enum(["perKgH2", "fixed"]).default("perKgH2"),
  required: z.boolean().default(true),
  help: z.string().optional(),
  source: z.string().optional(),
  /** 참고값들의 곱으로 채울 수 있는 항목(예: 경유 = 거리 × 왕복 × 연비). UI 도우미용 */
  computeHint: z.object({ product: z.array(z.string()) }).optional(),
});
export type TemplateInput = z.infer<typeof TemplateInputSchema>;

export const EquipmentTemplateSchema = z.object({
  id: z.string().min(1),
  /** 없으면 기술과 무관한 공통 설비(보조설비,압축기 등) */
  techId: z.string().optional(),
  stage: StageSchema,
  name: z.string().min(1),
  description: z.string().default(""),
  /** 아이콘 키 , 웹이 이름으로 그림을 고른다 */
  icon: z.string().default("box"),
  location: LocationSchema,
  defaultInBoundary: z.boolean().default(true),
  inputs: z.array(TemplateInputSchema),
  h2: z.object({
    role: z.enum(["producer", "passthrough", "none"]),
    /** passthrough 의 기본 손실률(0~1) */
    defaultLossRate: z.number().min(0).max(1).default(0),
  }),
  help: z.string().optional(),
  order: z.number().int().default(0),
  active: z.boolean().default(true),
});
export type EquipmentTemplate = z.infer<typeof EquipmentTemplateSchema>;

/* ── 산정방식 규칙 ───────────────────────────────────────────── */

export const GradeSchema = z.object({ grade: z.number().int(), maxKgCO2eq: z.number() });

export const MethodConfigSchema = z.object({
  id: MethodSchema,
  label: z.string(),
  description: z.string(),
  standards: z.array(z.string()),
  gwp: z.object({ basis: z.string(), co2: z.number(), ch4: z.number(), n2o: z.number() }),
  functionalUnit: z.object({
    locked: z.boolean(),
    h2Kg: z.number(),
    purityMinPct: z.number().optional(),
    pressureBar: z.number().optional(),
    state: z.enum(["gas", "liquid"]).optional(),
    deliveryPoint: z.string().optional(),
  }),
  boundary: z.object({
    locked: z.boolean(),
    description: z.string(),
    /** 산정에 포함하는 설비 위치. 나머지 위치의 설비는 강제 제외(cert_scope) */
    includeLocations: z.array(LocationSchema),
  }),
  /** 기능단위당 배출량의 분모: 최종 인도 수소량 / 생산시설 출하지점 수소량 */
  denominator: z.enum(["delivered", "gate"]),
  grades: z.array(GradeSchema).optional(),
  thresholdKgCO2eq: z.number().optional(),
  notices: z.array(z.string()),
});
export type MethodConfig = z.infer<typeof MethodConfigSchema>;

/* ── 교육용 예시 ─────────────────────────────────────────────── */

export const ReferenceResultSchema = z.object({
  totalKgCO2eq: z.number(),
  perKgH2: z.number(),
  denominatorKg: z.number(),
  status: DataStatusSchema.default("placeholder"),
  note: z.string().optional(),
});

export const ExampleSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().default(""),
  tags: z.array(z.string()).default([]),
  scenario: ScenarioSchema,
  /** 기준결과값 , 전문가 검산 전에는 엔진 계산값을 임시로 둔다 */
  reference: ReferenceResultSchema.nullable(),
  order: z.number().int().default(0),
  active: z.boolean().default(true),
});
export type Example = z.infer<typeof ExampleSchema>;

/* ── 용어 도움말 ─────────────────────────────────────────────── */

export const GlossaryEntrySchema = z.object({
  key: z.string().min(1),
  term: z.string().min(1),
  termEn: z.string().optional(),
  definition: z.string().min(1),
  source: z.string().min(1),
  related: z.array(z.string()).default([]),
});
export type GlossaryEntry = z.infer<typeof GlossaryEntrySchema>;

/** 프로그램이 한 번에 받아 쓰는 마스터 데이터 묶음 */
export const MasterDataSchema = z.object({
  factors: z.array(EmissionFactorSchema),
  sources: z.array(ElectricitySourceSchema),
  technologies: z.array(TechnologySchema),
  templates: z.array(EquipmentTemplateSchema),
  methods: z.array(MethodConfigSchema),
  examples: z.array(ExampleSchema),
  glossary: z.array(GlossaryEntrySchema),
});
export type MasterData = z.infer<typeof MasterDataSchema>;
